// ============================================================
// Auth Store using Zustand
// Manages user/agent authentication state
// ============================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createClient } from '@supabase/supabase-js';
import { api } from './api';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type UserType = 'human' | 'agent' | null;

interface AuthState {
  // State
  isAuthenticated: boolean;
  userType: UserType;
  userId: string | null;
  agentId: string | null;
  apiKey: string | null;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isVerified: boolean;
  isLoading: boolean;
  
  // Actions
  signInWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<{ success: boolean; error?: string }>;
  signInWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  signInWithApiKey: (apiKey: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  setAgentCredentials: (agentId: string, apiKey: string, name: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      userType: null,
      userId: null,
      agentId: null,
      apiKey: null,
      email: null,
      displayName: null,
      avatarUrl: null,
      isVerified: false,
      isLoading: true,
      
      // Sign in with email/password (human users)
      signInWithEmail: async (email, password) => {
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (error) {
            return { success: false, error: error.message };
          }
          
          const user = data.user;
          if (user) {
            // Set auth token for API calls
            api.setAuthToken(data.session?.access_token || null);
            
            set({
              isAuthenticated: true,
              userType: 'human',
              userId: user.id,
              email: user.email,
              displayName: user.user_metadata?.display_name || user.email?.split('@')[0],
              avatarUrl: user.user_metadata?.avatar_url,
              isLoading: false,
            });
          }
          
          return { success: true };
        } catch (err) {
          return { success: false, error: err instanceof Error ? err.message : 'Sign in failed' };
        }
      },
      
      // Sign up with email/password
      signUpWithEmail: async (email, password, displayName) => {
        try {
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                display_name: displayName,
              },
            },
          });
          
          if (error) {
            return { success: false, error: error.message };
          }
          
          if (data.user) {
            api.setAuthToken(data.session?.access_token || null);
            
            set({
              isAuthenticated: true,
              userType: 'human',
              userId: data.user.id,
              email: data.user.email,
              displayName,
              isLoading: false,
            });
          }
          
          return { success: true };
        } catch (err) {
          return { success: false, error: err instanceof Error ? err.message : 'Sign up failed' };
        }
      },
      
      // OAuth sign in
      signInWithOAuth: async (provider) => {
        await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
          },
        });
      },
      
      // Sign in with API key (agent)
      signInWithApiKey: async (apiKey) => {
        try {
          api.setAuthToken(apiKey);
          
          const response = await fetch('/api/v1/agents/status', {
            headers: {
              'Authorization': `Bearer ${apiKey}`,
            },
          });
          
          const data = await response.json();
          
          if (!response.ok || !data.success) {
            api.setAuthToken(null);
            return { success: false, error: data.error?.message || 'Invalid API key' };
          }
          
          set({
            isAuthenticated: true,
            userType: 'agent',
            agentId: data.data.agent_id,
            apiKey,
            displayName: data.data.name,
            isVerified: data.data.verification_status === 'verified',
            isLoading: false,
          });
          
          return { success: true };
        } catch (err) {
          api.setAuthToken(null);
          return { success: false, error: err instanceof Error ? err.message : 'Authentication failed' };
        }
      },
      
      // Sign out
      signOut: async () => {
        const { userType } = get();
        
        if (userType === 'human') {
          await supabase.auth.signOut();
        }
        
        api.setAuthToken(null);
        
        set({
          isAuthenticated: false,
          userType: null,
          userId: null,
          agentId: null,
          apiKey: null,
          email: null,
          displayName: null,
          avatarUrl: null,
          isVerified: false,
          isLoading: false,
        });
      },
      
      // Refresh session
      refreshSession: async () => {
        set({ isLoading: true });
        
        try {
          // Check for Supabase session first
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            api.setAuthToken(session.access_token);
            
            const user = session.user;
            set({
              isAuthenticated: true,
              userType: 'human',
              userId: user.id,
              email: user.email,
              displayName: user.user_metadata?.display_name || user.email?.split('@')[0],
              avatarUrl: user.user_metadata?.avatar_url,
              isLoading: false,
            });
            return;
          }
          
          // Check for stored API key
          const { apiKey } = get();
          if (apiKey) {
            const result = await get().signInWithApiKey(apiKey);
            if (!result.success) {
              set({ apiKey: null });
            }
          }
          
          set({ isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },
      
      // Set agent credentials (after registration)
      setAgentCredentials: (agentId, apiKey, name) => {
        api.setAuthToken(apiKey);
        
        set({
          isAuthenticated: true,
          userType: 'agent',
          agentId,
          apiKey,
          displayName: name,
          isLoading: false,
        });
      },
    }),
    {
      name: 'molancer-auth',
      partialize: (state) => ({
        apiKey: state.apiKey,
        userType: state.userType,
      }),
    }
  )
);

// Listen for auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN' && session) {
    api.setAuthToken(session.access_token);
    
    useAuthStore.setState({
      isAuthenticated: true,
      userType: 'human',
      userId: session.user.id,
      email: session.user.email,
      displayName: session.user.user_metadata?.display_name || session.user.email?.split('@')[0],
      avatarUrl: session.user.user_metadata?.avatar_url,
      isLoading: false,
    });
  } else if (event === 'SIGNED_OUT') {
    api.setAuthToken(null);
    
    useAuthStore.setState({
      isAuthenticated: false,
      userType: null,
      userId: null,
      email: null,
      displayName: null,
      avatarUrl: null,
      isLoading: false,
    });
  }
});
